// The gallery's tools (CCP Cam parity): photos or videos, one person's, newest or oldest first; and
// what a video still on its way up says (BL-082).

import { agoWords } from '../settings/devices-model.ts';
import { NOT_WHOLE } from './photo-display-model.ts';

export type GalleryKind = 'all' | 'photo' | 'video';
export type GallerySort = 'newest' | 'oldest';
export interface GalleryFilter {
  kind: GalleryKind;
  /** A person's name, or '' for everyone. */
  by: string;
  sort: GallerySort;
}
export const ALL: GalleryFilter = { kind: 'all', by: '', sort: 'newest' };

type Item = { kind: string; addedBy: string | null; capturedAt: string | null };

export function galleryOf<T extends Item>(items: readonly T[], f: GalleryFilter): T[] {
  const kept = items.filter((x) => (f.kind === 'all' || x.kind === f.kind) && (!f.by || x.addedBy === f.by));
  const t = (x: T) => (x.capturedAt ? Date.parse(x.capturedAt) : 0);
  return [...kept].sort((a, b) => (f.sort === 'newest' ? t(b) - t(a) : t(a) - t(b)));
}

/**
 * How many files a customer's Photos tab asks for (customers.photos' most): all of them, not the first
 * 200, which left an address's older photos and its videos out of the grid and the viewer (history fix 2).
 */
export const ALL_FILES = 5000;

/**
 * A customer's files by job, in the order the gallery put them (its first file's job first), and what
 * the viewer pages through: every photo and video in that same order, none left out; documents and
 * signatures stay in their own lists.
 */
export function filesByJob<T extends { jobId: string; jobNumber: string; kind: string }>(
  shown: readonly T[],
): { jobs: [string, string][]; viewable: T[] } {
  const jobs = [...new Map(shown.map((p) => [p.jobId, p.jobNumber])).entries()];
  const viewable = jobs.flatMap(([jobId]) =>
    shown.filter((p) => p.jobId === jobId && (p.kind === 'photo' || p.kind === 'video')),
  );
  return { jobs, viewable };
}

/** The people who added any, for the "Taken by" choice. */
export const takers = (items: readonly Item[]) =>
  [...new Set(items.map((x) => x.addedBy).filter((n): n is string => !!n))].sort((a, b) =>
    a.localeCompare(b),
  );

/** A video on its way up in parts (BL-082), as attachments.list and customers.photos give it. */
export interface VideoUpload {
  partsDone: number;
  partsTotal: number;
  lastPartAt: string | null;
  /** All in and joined: the server is reading it back to check it. */
  joined: boolean;
}

/** No part for this long: the upload has stopped (the iPad off Wi-Fi, out of signal, or closed). */
export const UPLOAD_PAUSED_MS = 15 * 60_000;

/**
 * What a video's tile says until it can be played: "Uploading, 56% (34 of 60 parts)", "Upload
 * paused: 34 of 60 parts, the last 3 h ago", "Checking the video…", "Waiting to upload" (the iPad
 * hasn't started: off Wi-Fi, say), or that it failed its check. Null once it's verified.
 */
export function videoUploadWords(
  p: { status: string; upload?: VideoUpload | null; checkFailed?: boolean },
  now: Date,
): string | null {
  if (p.status === 'verified') return null;
  if (p.checkFailed && p.status !== 'verified') return NOT_WHOLE;
  if (p.status === 'failed') return 'Didn’t arrive as it was taken';
  // sent in one piece, being read back (#73)
  if (p.status === 'checking') return 'Checking the video…';
  const u = p.upload;
  if (!u) return 'Waiting to upload';
  if (u.joined) return 'Checking the video…';
  const last = u.lastPartAt ? Date.parse(u.lastPartAt) : null;
  if (last === null) return `Upload started: 0 of ${u.partsTotal} parts`;
  if (now.getTime() - last < UPLOAD_PAUSED_MS)
    return `Uploading, ${Math.floor((u.partsDone / u.partsTotal) * 100)}% (${u.partsDone} of ${u.partsTotal} parts)`;
  return `Upload paused: ${u.partsDone} of ${u.partsTotal} parts, the last ${agoWords(now.getTime() - last)} ago`;
}
